import * as XLSX from 'xlsx';

// Helper function to check if a date is within fiscal year
const isWithinFiscalYear = (dateStr, fiscalYear) => {
    if (!dateStr || !fiscalYear?.start || !fiscalYear?.end) return false;
    
    try {
        const checkDate = new Date(dateStr);
        const fyStart = new Date(fiscalYear.start);
        const fyEnd = new Date(fiscalYear.end);
        
        return checkDate >= fyStart && checkDate <= fyEnd;
    } catch (error) {
        console.warn('Date parsing error:', dateStr);
        return false;
    }
};

// Get default fiscal year dates if not provided
const getDefaultDates = (fiscalYear) => {
    if (fiscalYear?.start && fiscalYear?.end) {
        return { start: fiscalYear.start, end: fiscalYear.end };
    }
    const currentYear = new Date().getFullYear();
    return { start: `${currentYear}-01-01`, end: `${currentYear}-12-31` };
};

/**
 * Creates a styled worksheet with auto-sized columns
 * Note: Basic XLSX has limited styling, so we focus on column widths
 * @param {Array} data - The data to convert to worksheet
 * @param {Array} columnWidths - Array of column width objects
 * @returns {object} XLSX worksheet object
 */
const createStyledWorksheet = (data, columnWidths) => {
  // Convert data to sheet
  const headers = Object.keys(data[0] || {});
  const rows = data.map(row => headers.map(h => row[h] || ''));
  const sheetData = [headers, ...rows];
  
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  
  // Set column widths (this works in standard xlsx)
  if (columnWidths && columnWidths.length > 0) {
    ws['!cols'] = columnWidths;
  }
  
  return ws;
};

/**
 * Auto-sizes columns based on content
 * @param {Array} data - The data array
 * @param {Object} options - Optional settings
 * @returns {Array} Column width array
 */
const calculateColumnWidths = (data, options = {}) => {
  if (!data || data.length === 0) return [];
  
  const { minWidth = 10, maxWidth = 50 } = options;
  const headers = Object.keys(data[0]);
  
  const widths = headers.map((header) => {
    // Start with header length
    let maxLength = header.length;
    
    // Check each row's content length for this column
    data.forEach(row => {
      const value = row[header] || '';
      const cellLength = String(value).length;
      if (cellLength > maxLength) {
        maxLength = Math.min(cellLength, maxWidth);
      }
    });
    
    // Calculate final width
    let width = maxLength + 2;
    if (width < minWidth) width = minWidth;
    if (width > maxWidth) width = maxWidth;
    
    return { wch: width };
  });
  
  return widths;
};


export const parseImportedObjectives = async (file, fiscalYear = null, isPastYearMigration = false) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const defaultDates = getDefaultDates(fiscalYear);
                const currentYear = new Date().getFullYear();

                // Exclude instruction sheets
                const sheetNames = workbook.SheetNames.filter(name => 
                    !name.includes('INSTRUCTION') && !name.includes('READ ME')
                );

                if (sheetNames.length === 0) {
                    reject(new Error('No objective sheets found.'));
                    return;
                }

                const objectives = [];

                sheetNames.forEach((sheetName) => {
                    const sheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                    if (jsonData.length === 0) return;

                    // Find first row with an objective name
                    const firstRow = jsonData.find(row => row['Objective Name']?.trim());
                    if (!firstRow) {
                        console.warn(`Sheet "${sheetName}" has no objective name, skipping.`);
                        return;
                    }

                    // Metadata from first row
                    let startDate = firstRow['Start Date'] || '';
                    let completionDate = firstRow['Completion Date'] || '';

                    if (fiscalYear?.start && fiscalYear?.end) {
                        const startInFY = isWithinFiscalYear(startDate, fiscalYear);
                        const endInFY = isWithinFiscalYear(completionDate, fiscalYear);
                        if (!startInFY && !endInFY && startDate && completionDate) {
                            startDate = fiscalYear.start;
                            completionDate = fiscalYear.end;
                        }
                        if (!startInFY && startDate) startDate = fiscalYear.start;
                        if (!endInFY && completionDate) completionDate = fiscalYear.end;
                    }
                    if (!startDate) startDate = defaultDates.start;
                    if (!completionDate) completionDate = defaultDates.end;

                    const objective = {
                        name: firstRow['Objective Name']?.trim() || '',
                        description: firstRow['Description']?.trim() || '',
                        weight: parseFloat(firstRow['Objective Weight (%)']) || 0,
                        startDate: startDate,
                        completionDate: completionDate,
                        status: firstRow['Status']?.trim() || 'draft',
                        performanceYear: fiscalYear?.start ? new Date(fiscalYear.start).getFullYear() : currentYear,
                        kras: [],
                        kpis: [],
                        _isImported: true,
                        _importedFrom: 'past_fy'
                    };

                    // Collect KPIs from all rows
                    const kpiCols = ['KPIs Name', 'KPI Description', 'KPI Measure'];
                    // Fallback to singular if needed
                    const hasPlural = jsonData.some(r => r['KPIs Name'] !== undefined);
                    const nameCol = hasPlural ? 'KPIs Name' : 'KPI Name';
                    const descCol = hasPlural ? 'KPI Description' : 'KPI Description';
                    const measCol = hasPlural ? 'KPI Measure' : 'KPI Measure';

                    jsonData.forEach(row => {
                        const kpiName = row[nameCol]?.toString().trim() || '';
                        const kpiDesc = row[descCol]?.toString().trim() || '';
                        const kpiMeas = row[measCol]?.toString().trim() || '';
                        if (kpiName || kpiDesc || kpiMeas) {
                            objective.kpis.push({
                                name: kpiName,
                                description: kpiDesc,
                                target: kpiMeas
                            });
                        }
                    });

                    if (objective.name) {
                        objectives.push(objective);
                    }
                });

                console.log(`✅ Parsed ${objectives.length} objectives`);
                console.log(`📊 Total KPIs: ${objectives.reduce((sum, o) => sum + o.kpis.length, 0)}`);

                if (objectives.length === 0) {
                    reject(new Error('No valid objectives found.'));
                } else {
                    resolve(objectives);
                }
            } catch (error) {
                console.error('Parse error:', error);
                reject(error);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
};


// Helper: Parse KPIs from text format
const parseKpisFromText = (kpisText) => {
    if (!kpisText) return [];
    return kpisText.split(';').filter(k => k.trim()).map(k => {
        const targetMatch = k.match(/Target\s*:\s*([^;]+)/i) || k.match(/Target\s*(\S+)/i);
        let name = k;
        let target = '';
        
        if (targetMatch) {
            target = targetMatch[1].trim();
            name = k.replace(targetMatch[0], '').trim();
        }
        
        // Clean up name
        name = name.replace(/Target\s*:/i, '').replace(/:\s*$/, '').trim();
        
        return {
            name: name || 'Unnamed KPI',
            description: '',
            target: target
        };
    });
};

// Create template for download with separate sheets - Single KPI column
export const downloadObjectiveTemplate = (fiscalYear) => {
    const fiscalYearRange = fiscalYear?.start && fiscalYear?.end 
        ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
        : `${new Date().getFullYear()} - ${new Date().getFullYear() + 1}`;
    
    const defaultStartDate = fiscalYear?.start || `${new Date().getFullYear()}-01-01`;
    const defaultEndDate = fiscalYear?.end || `${new Date().getFullYear()}-12-31`;

    // ----- INSTRUCTION SHEET -----
    const instructionsData = [
        { 'Step': '1', 'Instruction': 'Each sheet = one objective', 'Details': 'Duplicate a sheet to add more objectives' },
        { 'Step': '2', 'Instruction': 'First row = objective metadata', 'Details': 'Objective Name, Description, Weight, Dates, Status' },
        { 'Step': '3', 'Instruction': 'Every row = one KPI', 'Details': 'Fill in KPIs Name, Description, Measure in each row' },
        { 'Step': '4', 'Instruction': 'Only Objective Name is REQUIRED', 'Details': 'Weight must be 1‑100% if provided' },
        { 'Step': '5', 'Instruction': 'Dates are OPTIONAL', 'Details': `Will use ${defaultStartDate} to ${defaultEndDate} if blank` },
        { 'Step': '6', 'Instruction': '⚠️ AFTER IMPORT', 'Details': 'You MUST link objectives to current goals in the web app' },
    ];

    // Helper to build sheet rows for one objective
    const buildObjectiveSheet = (objName, objDesc, objWeight, kpis) => {
        const rows = kpis.map((kpi, index) => ({
            'Objective Name': index === 0 ? objName : '',
            'Description': index === 0 ? objDesc : '',
            'Objective Weight (%)': index === 0 ? objWeight : '',
            'Start Date': index === 0 ? defaultStartDate : '',
            'Completion Date': index === 0 ? defaultEndDate : '',
            'Status': index === 0 ? 'draft' : '',
            'KPIs Name': kpi.name,
            'KPI Description': kpi.description,
            'KPI Measure': kpi.target
        }));
        // One blank row for extra KPI
        rows.push({
            'Objective Name': '', 'Description': '', 'Objective Weight (%)': '',
            'Start Date': '', 'Completion Date': '', 'Status': '',
            'KPIs Name': '', 'KPI Description': '', 'KPI Measure': ''
        });
        return rows;
    };

    // ---- Objective 1: Sales (matches your image) ----
    const salesKpis = [
        { name: 'Increase sales', description: 'Increased sales', target: '100 bags more sold compared to last year' },
        { name: 'Shelves are always full', description: 'Shelves are always full', target: 'Shelves are always full more than 75%' }
    ];
    const salesRows = buildObjectiveSheet(
        'Sales',
        'Sales are increased and shelves are always full',
        50,
        salesKpis
    );

    // ---- Objective 2: Customer Satisfaction ----
    const csKpis = [
        { name: 'Customer satisfaction score', description: 'Measured via post‑service surveys', target: '≥ 90% satisfied' },
        { name: 'Response time', description: 'Time from inquiry to first response', target: 'Under 2 hours' }
    ];
    const csRows = buildObjectiveSheet(
        'Customer Satisfaction',
        'Improve customer satisfaction through faster service',
        50,
        csKpis
    );

    const wb = XLSX.utils.book_new();
    const colWidths = [
        { wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
        { wch: 10 }, { wch: 25 }, { wch: 40 }, { wch: 20 }
    ];

    // Instructions
    const instructionsWs = XLSX.utils.json_to_sheet(instructionsData);
    instructionsWs['!cols'] = [{ wch: 8 }, { wch: 35 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'INSTRUCTIONS');

    // Sales sheet
    const salesWs = XLSX.utils.json_to_sheet(salesRows);
    salesWs['!cols'] = colWidths;
    salesWs['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, salesWs, 'Sales Objective');

    // Customer Satisfaction sheet
    const csWs = XLSX.utils.json_to_sheet(csRows);
    csWs['!cols'] = colWidths;
    csWs['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, csWs, 'Customer Satisfaction');

    XLSX.writeFile(wb, `objectives_template_${fiscalYearRange.replace(' - ', '_')}.xlsx`);
};

// Export past year objectives with full KPI structure
export const exportPastYearObjectivesToExcel = (pastYearObjectives, fiscalYear) => {
    const currentFyRange = fiscalYear?.start && fiscalYear?.end 
        ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
        : `${new Date().getFullYear()} - ${new Date().getFullYear() + 1}`;
    
    const instructionsData = [
        { 'Step': '1', 'Instruction': 'Each sheet = one objective', 'Details': 'Review before importing' },
        { 'Step': '2', 'Instruction': 'First row holds objective metadata', 'Details': 'Next rows contain KPIs' },
        { 'Step': '3', 'Instruction': '⚠️ AFTER IMPORT', 'Details': 'Re‑link each objective to current goals' },
    ];
    
    const wb = XLSX.utils.book_new();
    const colWidths = [
        { wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
        { wch: 10 }, { wch: 25 }, { wch: 40 }, { wch: 20 }
    ];

    // Instructions
    const instructionsWs = XLSX.utils.json_to_sheet(instructionsData);
    instructionsWs['!cols'] = [{ wch: 10 }, { wch: 35 }, { wch: 55 }];
    XLSX.utils.book_append_sheet(wb, instructionsWs, 'INSTRUCTIONS_READ_ME');

    // One sheet per objective
    pastYearObjectives.forEach((obj, idx) => {
        const kpis = obj.kpis?.length ? obj.kpis : [{ name: '', description: '', target: '' }];
        const rows = kpis.map((kpi, i) => ({
            'Objective Name': i === 0 ? (obj.name || '') : '',
            'Description': i === 0 ? (obj.description || '') : '',
            'Objective Weight (%)': i === 0 ? (obj.weight || 0) : '',
            'Start Date': i === 0 ? (fiscalYear?.start || obj.startDate || '') : '',
            'Completion Date': i === 0 ? (fiscalYear?.end || obj.completionDate || '') : '',
            'Status': i === 0 ? 'draft' : '',
            'KPIs Name': kpi.name || '',
            'KPI Description': kpi.description || '',
            'KPI Measure': kpi.target || ''
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = colWidths;
        ws['!freeze'] = { xSplit: 0, ySplit: 1 };

        let sheetName = obj.name ? obj.name.substring(0, 31) : `Objective ${idx + 1}`;
        if (wb.SheetNames.includes(sheetName)) {
            sheetName = `${sheetName} ${idx + 1}`;
        }
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `past_fy_migration_to_${currentFyRange.replace(' - ', '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
};